import { Warning, Trash, Copy } from "@phosphor-icons/react";
import ModalCloseButton from "./ModalCloseButton";
import "./ConfirmDialog.css";

/**
 * Shared Confirmation Dialog Component
 * @param {boolean} isOpen - Whether the dialog is open
 * @param {function} onClose - Function to close the dialog
 * @param {function} onConfirm - Function to call when confirmed
 * @param {string} title - Dialog title
 * @param {string} message - Dialog message
 * @param {string} confirmText - Text for confirm button (default: "אישור")
 * @param {string} cancelText - Text for cancel button (default: "ביטול")
 * @param {string} type - Type of dialog: "danger" | "warning" | "info" (default: "warning")
 * @param {string} icon - Icon type: "delete" | "duplicate" | "warning" (default: "warning")
 * @param {boolean} confirmDisabled - Disable the confirm button (e.g. while validating)
 * @param {boolean} hideConfirm - Hide the confirm button entirely (blocking/informational dialog)
 * @param {ReactNode} children - Optional body rendered between the message and the actions
 *                               (e.g. an input the user fills before confirming)
 */
function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title = "אישור פעולה",
  message = "האם אתה בטוח?",
  confirmText = "אישור",
  cancelText = "ביטול",
  type = "warning",
  icon = "warning",
  confirmDisabled = false,
  hideConfirm = false,
  children,
}) {
  if (!isOpen) return null;

  const handleConfirm = (e) => {
    e.stopPropagation();
    if (confirmDisabled) return;
    onConfirm();
    onClose();
  };

  const handleOverlayClick = (e) => {
    e.stopPropagation(); // Prevent click from bubbling to parent modals
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const renderIcon = () => {
    switch (icon) {
      case "delete":
        return <Trash size={28} weight="duotone" />;
      case "duplicate":
        return <Copy size={28} weight="duotone" />;
      default:
        return <Warning size={28} weight="duotone" />;
    }
  };

  return (
    <div className="confirm-dialog-overlay" onClick={handleOverlayClick}>
      <div className={`confirm-dialog confirm-dialog-${type}`} onClick={(e) => e.stopPropagation()}>
        <ModalCloseButton onClick={(e) => { e.stopPropagation(); onClose(); }} />

        <div className={`confirm-dialog-icon confirm-dialog-icon-${type}`}>
          {renderIcon()}
        </div>

        <h3 className="confirm-dialog-title">{title}</h3>
        <p className="confirm-dialog-message">{message}</p>

        {children && <div className="confirm-dialog-body">{children}</div>}

        <div className="confirm-dialog-actions">
          <button className="confirm-dialog-btn cancel" onClick={(e) => { e.stopPropagation(); onClose(); }}>
            {cancelText}
          </button>
          {!hideConfirm && (
            <button
              className={`confirm-dialog-btn confirm confirm-${type}`}
              onClick={handleConfirm}
              disabled={confirmDisabled}
            >
              {confirmText}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ConfirmDialog;

